import { useState } from "react";
import { toast } from "sonner";
import { Check, Download, Loader2, Send } from "lucide-react";
import { WhatsAppGlyph } from "@/components/cards/CardShell";
import { STOCK, ron } from "@/lib/bf";
import { campFile } from "./BFCampaignCard";

const line = (cfg, c) => {
  const b = cfg.brands.find((x) => x.id === c.brand)?.name || "";
  const tot = (c.min_list || 0) + (c.surprise_value || 0);
  return c.surprise_value > 0 ? `• ${b}: comandă de ${ron(tot)} cu -${c.discount}%, plătiți ${ron(Math.round(tot * (100 - c.discount)) / 100)} (alegeți ${ron(c.min_list)} + ${ron(c.surprise_value)} produse utile alese de noi)`
    : `• ${b}: -${c.discount}%${c.min_list > 0 ? ` la comenzi de minim ${ron(c.min_list)}` : ""}`;
};

export default function BFOfferPicker({ offers, cfg, camp, agent, shareText, url }) {
  const [sel, setSel] = useState(() => offers.map((c) => c.id));
  const [ready, setReady] = useState(null);
  const [busy, setBusy] = useState(false);
  const picked = offers.filter((c) => sel.includes(c.id));
  const all = sel.length === offers.length;
  const toggle = (id) => { setReady(null); setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])); };
  const text = picked.length === 1 ? shareText(picked[0])
    : `Bună ziua! Ofertele Black Friday Beauty Trends pentru salonul dumneavoastră:\n${picked.map((c) => line(cfg, c)).join("\n")}\nComandă aici: ${url}${agent?.name ? `\n${agent.name}${agent.phone ? ` · ${agent.phone}` : ""}` : ""}\n${STOCK}.`;
  const fallback = (files) => {
    files.forEach((f) => Object.assign(document.createElement("a"), { href: URL.createObjectURL(f), download: f.name }).click());
    setReady("wa");
    toast.success(`${files.length} carduri descărcate. Deschide WhatsApp și atașează-le din galerie.`);
  };
  const prepare = async () => {
    setBusy(true);
    try {
      const files = await camp.files(picked, (c) => campFile(c, agent));
      if (navigator.canShare?.({ files })) setReady(files); else fallback(files);
    } catch { toast.error("Nu am putut genera cardurile"); }
    setBusy(false);
  };
  const share = () => navigator.share({ files: ready, text }).then(() => setReady(null))
    .catch((e) => { if (e?.name !== "AbortError") toast.error("Nu am putut deschide trimiterea. Încearcă din nou."); });
  const download = async () => {
    setBusy(true);
    let n = 0;
    for (const c of picked) n += (await camp.run(c, campFile(c, agent), true)) ? 1 : 0;
    toast[n === picked.length ? "success" : "warning"](`${n} din ${picked.length} carduri descărcate`);
    setBusy(false);
  };
  return (
    <div className="bf-picker" data-testid="bf-offer-picker">
      <div className="bf-picker-head">
        <b>Alege ofertele de trimis ({picked.length} din {offers.length})</b>
        <button type="button" className="bf-gate-switch" onClick={() => { setReady(null); setSel(all ? [] : offers.map((c) => c.id)); }} data-testid="bf-picker-all">{all ? "Deselectează tot" : "Selectează toate"}</button>
      </div>
      <div className="bf-picker-list">
        {offers.map((c) => {
          const on = sel.includes(c.id);
          return (
            <button key={c.id} type="button" className={`bf-picker-chip ${on ? "is-on" : ""}`} onClick={() => toggle(c.id)} aria-pressed={on} data-testid={`bf-picker-${c.brand}`}>
              {on && <Check size={13} />} {cfg.brands.find((b) => b.id === c.brand)?.name} <small>-{c.discount}%</small>
            </button>
          );
        })}
      </div>
      <div className="share-actions">
        {ready === "wa" ? (
          <a className="st-btn st-btn--light bf-agent-wa" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" onClick={() => setReady(null)} data-testid="bf-picker-open-wa">
            <WhatsAppGlyph /> Deschide WhatsApp cu textul
          </a>
        ) : ready ? (
          <button type="button" className="st-btn st-btn--light bf-agent-wa" onClick={share} data-testid="bf-picker-share">
            <WhatsAppGlyph /> Trimite {ready.length} {ready.length === 1 ? "card" : "carduri"} pe WhatsApp
          </button>
        ) : (
          <button type="button" className="st-btn st-btn--light bf-agent-wa" disabled={!picked.length || busy || !!camp.item} onClick={prepare} data-testid="bf-picker-prepare">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Trimite pe WhatsApp ({picked.length})
          </button>
        )}
        <button type="button" className="st-btn st-btn--ghost" disabled={!picked.length || busy || !!camp.item} onClick={download} data-testid="bf-picker-download">
          <Download size={14} /> Descarcă ({picked.length})
        </button>
      </div>
      {ready && ready !== "wa" && <p className="st-hint" data-testid="bf-picker-ready">Cardurile sunt gata. Apasă butonul verde și alege WhatsApp, apoi salonul sau grupul.</p>}
    </div>
  );
}
